/**
 * Ghost Theme Multilingual System - Simplified
 * Handles language detection, switching, and localStorage persistence
 * Filtering and translations are handled by Handlebars templates
 */

(function() {
    'use strict';

    const MultiLang = {
        // Configuration
        config: {
            defaultLang: 'en',
            availableLangs: ['en'],
            storageKey: 'ghost-theme-lang'
        },

        // Current language
        currentLang: null,

        /**
         * Initialize the multilingual system
         */
        init: function() {
            // Get configuration from meta tag
            var configEl = document.querySelector('meta[name="multilang-config"]');
            if (configEl) {
                this.config.defaultLang = configEl.dataset.defaultLang || 'en';
                this.config.availableLangs = (configEl.dataset.availableLangs || 'en').split(',').map(function(l) {
                    return l.trim();
                });
            }

            // Detect current language
            this.currentLang = this.detectLanguage();

            // Store and set language
            this.setLanguage(this.currentLang);

            // Initialize language switcher buttons
            this.initLanguageSwitcher();
            this.updateLanguageSwitcher();
        },

        /**
         * Detect the current language from various sources
         */
        detectLanguage: function() {
            // 1. Check URL path (e.g., /de/themen/ or /en/themes/)
            var path = window.location.pathname;
            var pathMatch = path.match(/^\/([a-z]{2})\//);
            if (pathMatch && this.config.availableLangs.indexOf(pathMatch[1]) !== -1) {
                return pathMatch[1];
            }

            // 2. Check URL parameter (?lang=de)
            var urlParams = new URLSearchParams(window.location.search);
            var urlLang = urlParams.get('lang');
            if (urlLang && this.config.availableLangs.indexOf(urlLang) !== -1) {
                return urlLang;
            }

            // 3. Check localStorage
            var storedLang = localStorage.getItem(this.config.storageKey);
            if (storedLang && this.config.availableLangs.indexOf(storedLang) !== -1) {
                return storedLang;
            }

            // 4. Check browser language
            var browserLang = navigator.language.split('-')[0];
            if (this.config.availableLangs.indexOf(browserLang) !== -1) {
                return browserLang;
            }

            // 5. Return default
            return this.config.defaultLang;
        },

        /**
         * Set the current language
         */
        setLanguage: function(lang) {
            // Validate language
            if (this.config.availableLangs.indexOf(lang) === -1) {
                lang = this.config.defaultLang;
            }

            this.currentLang = lang;

            // Save to localStorage
            localStorage.setItem(this.config.storageKey, lang);

            // Update HTML lang attribute
            document.documentElement.setAttribute('lang', lang);

            // Emit custom event for other scripts
            var event;
            if (typeof CustomEvent === 'function') {
                event = new CustomEvent('languageChanged', {
                    detail: { language: lang },
                    bubbles: true,
                    cancelable: true
                });
            } else {
                // Fallback for older browsers
                event = document.createEvent('CustomEvent');
                event.initCustomEvent('languageChanged', true, true, { language: lang });
            }
            document.dispatchEvent(event);
        },

        /**
         * Initialize language switcher buttons
         */
        initLanguageSwitcher: function() {
            var self = this;
            var switchers = document.querySelectorAll('.lang-switcher-btn');
            switchers.forEach(function(btn) {
                btn.addEventListener('click', function(e) {
                    e.preventDefault();
                    var lang = btn.dataset.lang;
                    self.switchLanguage(lang);
                });
            });
        },

        /**
         * Update language switcher UI to show active language
         */
        updateLanguageSwitcher: function() {
            var self = this;
            var switchers = document.querySelectorAll('.lang-switcher-btn');
            switchers.forEach(function(btn) {
                var lang = btn.dataset.lang;
                if (lang === self.currentLang) {
                    btn.classList.add('active');
                    btn.setAttribute('aria-current', 'true');
                } else {
                    btn.classList.remove('active');
                    btn.removeAttribute('aria-current');
                }
            });
        },

        /**
         * Switch to a different language
         */
        switchLanguage: function(newLang) {
            if (newLang === this.currentLang) return;

            // Validate language
            if (this.config.availableLangs.indexOf(newLang) === -1) {
                console.warn('Language not available:', newLang);
                return;
            }

            // Save new language
            this.setLanguage(newLang);

            // Try to find equivalent page using navigation-translations.js
            var targetUrl = '/' + newLang + '/'; // Default: go to language home
            var currentPath = window.location.pathname;

            // Normalize current path
            if (currentPath !== '/' && !currentPath.endsWith('/')) {
                currentPath += '/';
            }

            // Check if current page has a defined translation
            if (window.navigationTranslations) {
                var foundKey = null;

                // Search through all navigation entries to find which one matches current path
                for (var baseKey in window.navigationTranslations) {
                    if (!window.navigationTranslations.hasOwnProperty(baseKey)) continue;

                    var navEntry = window.navigationTranslations[baseKey];

                    // Check if current path matches any language variant of this entry
                    for (var lang in navEntry) {
                        if (!navEntry.hasOwnProperty(lang)) continue;

                        if (navEntry[lang].url === currentPath) {
                            foundKey = baseKey;
                            break;
                        }
                    }

                    if (foundKey) break;
                }

                // If we found a matching entry, get the translation for the new language
                if (foundKey && window.navigationTranslations[foundKey][newLang]) {
                    targetUrl = window.navigationTranslations[foundKey][newLang].url;
                    console.log('Switching from', currentPath, 'to', targetUrl);
                }
            }

            // Navigate to the target URL
            window.location.href = targetUrl;
        }
    };

    // Auto-initialize on DOMContentLoaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            MultiLang.init();
        });
    } else {
        MultiLang.init();
    }

    // Expose to global scope
    window.MultiLang = MultiLang;
})();
